














































































































































































































































import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { ButtonState } from "@/components/common/StateButton.vue";

import { formatApartmentName } from "@/utils/LocalisationHelper";

import {
  ProjectDetails,
  ContactPersonInfo,
  mapApartmentsForElectricityBillingToContactPersons,
  isOnlyUsedForElectricityBilling,
  isElectricityBillingContactPersonId,
  isPortalOnlyProject,
  stripElectricityBillingPrefix
} from "@/data/models/ProjectDetails";
import { ClientPanelAssignment } from "@/data/models/ClientPanelAssignment";

import { getClientPanel } from "@/data/repository/ClientPanelRepository";
import { getGenericDeviceDetails } from "@/data/repository/GenericDeviceRepository";

import DeviceProjectAssignmentItemComponent from "@/components/project/devices/DeviceProjectAssignmentItem.vue";
import ClientDataSectionComponent from "@/components/project/clientdata/ClientDataSection.vue";
import { ProjectDetailsTab } from "@/data/models/ProjectDetailsTab";
import { getActiveUser } from "@/data/repository/UserRepository";
import { GenericDeviceProjectAssignment } from "@/data/models/GenericDeviceAssignment";
import { alwaysAllowedToAssignDevicesAccess } from "@/permissions/UserPermissions";
import {
  DeviceProjectAssignment,
  getIndependantDeviceProjectAssignments,
  getDependantDeviceProjectAssignments,
  compareDeviceProjectAssignments,
  isClientPanelDeviceProjectAssignment,
} from "@/data/models/DeviceProjectAssignment";
import { COLOR_HY_DRY_DEVICE_ASSIGNMENT_LINK, COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { themed } from "@/utils/Theme";
import { DeviceOverview } from "@/data/models/DeviceOverview";
import { Room } from "@/data/models/ProjectDetails";
import { formatRoomName } from "@/data/models/ProjectLog";
import { getRoomUsageOptions, loadRoomUsageOptions } from "@/data/repository/RoomUsageOptionsRepository";
import { ProjectManagementSettingsModule } from "@/store/modules/ProjectManagementSettingsModule";
import {
  canAssignDeviceToApartment,
  getAssignableRoomsOfApartment,
  groupAssignmentsByRoom,
  RoomAssignmentSection,
  usesRoomLevelDeviceAssignment,
} from "@/utils/RoomLevelDeviceAssignment";

@Component({
  components: {
    "client-data-section": ClientDataSectionComponent,
    "device-project-assignment": DeviceProjectAssignmentItemComponent,
  },
})
export default class ProjectDevicesComponent extends Vue {
  @Prop()
  projectDetails!: ProjectDetails;

  @Prop({ default: false })
  showCompactVersion!: boolean;

  @Prop({ default: true })
  showHeader!: boolean;

  @Prop({ default: true })
  showEditOptions!: boolean;

  @Prop({ default: true })
  canEditAssignments!: boolean;

  @Prop({ default: true })
  showApartmentNames!: boolean;

  @Prop({ default: false })
  isReadOnlyMode!: boolean;

  @Prop({ default: true })
  isDeviceAssignmentInAllowedProjectState!: boolean;

  @Prop()
  assignmentHintTitleText!: string;

  @Prop({ default: [] })
  clientPanelAssignments!: Array<ClientPanelAssignment>;

  @Prop({ default: [] })
  genericDeviceAssignments!: Array<GenericDeviceProjectAssignment>;

  @Prop({ default: ProjectDetailsTab.DEVICES })
  usedInTab!: ProjectDetailsTab;

  lastDisplayedDate: Date = new Date();

  /** Phone layout (below 600 px): headings with the notice under the name, rows as list rows. */
  get isPhone() {
    return this.$vuetify.breakpoint.xsOnly;
  }

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  get dependantAssignmentIconColor() {
    return COLOR_HY_DRY_DEVICE_ASSIGNMENT_LINK;
  }

  get activeUser() {
    return getActiveUser();
  }

  get isAlwaysAllowedToAssignDevices() {
    return alwaysAllowedToAssignDevicesAccess(this.activeUser);
  }

  get canEditProject() {
    return !this.projectDetails.projectConclusionData?.projectConclusionDate;
  }

  get clientData() {
    return this.projectDetails.clientData;
  }

  get setupDevicesData() {
    return this.projectDetails.setupDevicesData;
  }

  get apartmentsForElectricityBilling() {
    return mapApartmentsForElectricityBillingToContactPersons(
      this.setupDevicesData?.apartmentsForElectricityBilling ?? []
    );
  }

  get contactPersons() {
    const persons = [
      ...this.clientData.contactPersons,
      ...this.apartmentsForElectricityBilling
    ];
    return persons;
  }

  get isProjectFinished() {
    return !!this.projectDetails.projectConclusionData?.projectConclusionDate;
  }

  isApartmentForElectricityBilling(contactPerson: ContactPersonInfo): boolean {
    return isOnlyUsedForElectricityBilling(contactPerson);
  }

  /** Whether devices are assigned to rooms (K7), read from the live settings. */
  get isRoomLevelMode() {
    return usesRoomLevelDeviceAssignment(ProjectManagementSettingsModule.projectManagementSettings);
  }

  /**
   * Rooms of the apartment that take devices in room mode; none outside room mode and for
   * Strombezug apartments, which always stay apartment-level.
   */
  getAssignableRooms(contactPerson: ContactPersonInfo): Room[] {
    if (!this.isRoomLevelMode || this.isApartmentForElectricityBilling(contactPerson)) {
      return [];
    }

    return getAssignableRoomsOfApartment(
      this.projectDetails.damagePatternData?.rooms ?? [],
      contactPerson.id,
      this.clientData.contactPersons.length === 1
    );
  }

  /**
   * In room mode a device is assigned from its room's row, so the apartment-level button is
   * disabled, see {@link canAssignDeviceToApartment} for the no-rooms exception on portal-only projects.
   */
  canAssignToApartment(contactPerson: ContactPersonInfo) {
    return canAssignDeviceToApartment(
      this.isRoomLevelMode,
      this.isApartmentForElectricityBilling(contactPerson),
      this.getAssignableRooms(contactPerson).length,
      isPortalOnlyProject(this.projectDetails)
    );
  }

  apartmentAssignmentDisabledHint(contactPerson: ContactPersonInfo) {
    return this.getAssignableRooms(contactPerson).length > 0
      ? this.$t("assignDevice.roomLevelAssignToRoomHint").toString()
      : this.$t("assignDevice.roomLevelNoRoomsHint").toString();
  }

  /** The Beschreibung version a room usage picked from the list is read against; only room mode shows rooms. */
  get roomUsageOptionsVersion() {
    return this.isRoomLevelMode ? this.projectDetails.damagePatternData?.roomInfoConfigVersion ?? null : null;
  }

  @Watch("roomUsageOptionsVersion", { immediate: true })
  onRoomUsageOptionsVersionChanged() {
    loadRoomUsageOptions(this.roomUsageOptionsVersion);
  }

  getRoomName(room: Room) {
    return formatRoomName(this, room.roomNumber, room, getRoomUsageOptions(this.roomUsageOptionsVersion));
  }

  /**
   * The blocks the apartment's device list is rendered in: outside room mode a single room-less
   * block, which is exactly the flat list; in room mode the room-less devices first, then every
   * room, see {@link groupAssignmentsByRoom}.
   */
  getAssignmentSections(contactPerson: ContactPersonInfo): RoomAssignmentSection<DeviceProjectAssignment>[] {
    const assignments = this.getDeviceProjectAssignmentsOfContactPerson(contactPerson);

    if (!this.isRoomLevelMode || this.isApartmentForElectricityBilling(contactPerson)) {
      return [{ room: null, assignments }];
    }

    return groupAssignmentsByRoom(assignments, this.getAssignableRooms(contactPerson));
  }

  async onEditClientPanelAssignment(
    contactPerson: ContactPersonInfo,
    deviceProjectAssignment: DeviceProjectAssignment
  ) {
    if (!this.isClientPanelAssignment(deviceProjectAssignment)) {
      const genericDeviceAssignment = deviceProjectAssignment as GenericDeviceProjectAssignment;
      const genericDevice = await getGenericDeviceDetails(genericDeviceAssignment.assignedDeviceId);

      this.$emit(
        "onEditAssignment",
        genericDeviceAssignment,
        {
          availability: genericDevice.availability,
          branch: genericDevice.branch,
          categoryId: genericDeviceAssignment.assignedDeviceCategoryId ?? genericDevice.categoryId,
          currentlyAssigned: !genericDeviceAssignment.assignmentEndDate,
          deleted: genericDevice.flaggedAsDeleted,
          id: genericDevice.id,
          inventoryNumber: genericDevice.inventoryNumber,
          serialNumber: genericDevice.serialNumber ?? "",
          templateId: genericDeviceAssignment.assignedDeviceTemplateId ?? genericDevice.templateId,
        } as unknown as DeviceOverview,
        contactPerson,
        undefined,
        undefined,
        this.hasDependantAssignments(contactPerson, deviceProjectAssignment)
      );
      return;
    }

    const clientPanelAssignment = deviceProjectAssignment as ClientPanelAssignment;

    const clientPanel = await getClientPanel(
      clientPanelAssignment.serialNumber
    );

    if (clientPanel) {
      this.$emit(
        "onEditAssignment",
        clientPanelAssignment,
        clientPanel,
        contactPerson,
        undefined,
        undefined,
        this.hasDependantAssignments(contactPerson, deviceProjectAssignment)
      );
    }
  }

  async onAssignDevices(contactPerson: ContactPersonInfo, roomId: string | null = null) {
    this.$emit("onAssignDevices", contactPerson, roomId);
  }

  areDependantAssignmentsReleased(
    contactPerson: ContactPersonInfo,
    deviceProjectAssignment: DeviceProjectAssignment
  ) {
    if (deviceProjectAssignment.electricityMeterForOtherDevices) {
      return !this.getDeviceProjectAssignmentsOfContactPerson(contactPerson)
        .find((assignment) => 
          assignment.externalElectricityMeterInfo?.assignmentId === deviceProjectAssignment.id &&
          !assignment.assignmentEndDate
        );
    }

    return true;
  }

  hasDependantAssignments(
    contactPerson: ContactPersonInfo,
    deviceProjectAssignment: DeviceProjectAssignment
  ) {
    if (deviceProjectAssignment.electricityMeterForOtherDevices) {
      return this.getDeviceProjectAssignmentsOfContactPerson(contactPerson)
        .find((assignment) => 
          assignment.externalElectricityMeterInfo?.assignmentId === deviceProjectAssignment.id
        );
    }

    return false;
  }

  isClientPanelAssignment(deviceProjectAssignment: DeviceProjectAssignment) {
    return isClientPanelDeviceProjectAssignment(deviceProjectAssignment);
  }

  isDependantAssignment(deviceProjectAssignment: DeviceProjectAssignment) {
    return !!deviceProjectAssignment.externalElectricityMeterInfo;
  }

  getApartmentName(contactPersonInfo: ContactPersonInfo) {
    if (this.showCompactVersion) {
      if (this.showApartmentNames) {
        // Electricity-billing apartments are numbered in their own namespace, so they must not be
        // labelled "Wohnung" — the project app calls them "Strombezug".
        const key = isOnlyUsedForElectricityBilling(contactPersonInfo)
          ? "assignDevice.formatHyDryInElectricitySupply"
          : "assignDevice.formatHyDryInApartment";

        return this.$t(key, [contactPersonInfo.contactPersonNumber]).toString();
      } else {
        return this.$t("common.hydryDevices").toString();
      }
    } else {
      return formatApartmentName(this, contactPersonInfo, this.clientData.contactPersons.length > 1);
    }
  }

  /**
   * Whether the apartment shows "keine Geräte zugeordnet": it has no devices and no rooms that
   * could show the notice for it. In room mode an empty room carries the notice in its own row.
   */
  hasNoDevicesInApartment(contactPerson: ContactPersonInfo) {
    return !this.hasContactPersonDeviceProjectAssignments(contactPerson) && this.getAssignableRooms(contactPerson).length === 0;
  }

  hasContactPersonDeviceProjectAssignments(contactPerson: ContactPersonInfo) {
    return (
      this.getDeviceProjectAssignmentsOfContactPerson(contactPerson).length > 0
    );
  }

  /**
   * Retrieves the device project assignments for a given contact person.
   *
   * This function filters and combines client panel assignments and generic device assignments 
   * for the specified contact person. The assignments are then categorized into independent 
   * and dependent device project assignments, sorted, and returned in a specific order.
   * Which generic assignments are shown at all is decided by the caller (`genericDeviceAssignments`),
   * see `isGenericDeviceAssignmentShown`.
   *
   * @param {ContactPersonInfo} contactPerson - The contact person whose device project assignments are to be retrieved.
   * @returns {DeviceProjectAssignment[]} The sorted list of device project assignments for the contact person.
   */
  getDeviceProjectAssignmentsOfContactPerson(contactPerson: ContactPersonInfo) {
    const isSingleContactPerson = this.clientData.contactPersons.length === 1
      && !this.isApartmentForElectricityBilling(contactPerson);

    // Both sides are stripped: assignments written by the project app carry the
    // ELECTRICITY_BILLING_ prefix, the real contact persons never do.
    const contactPersonId = stripElectricityBillingPrefix(contactPerson.id);

    // Filters client panel assignments for a given contact person.
    // When there is only one (non-electricity-billing) contact person, all non-electricity-billing
    // assignments belong to it regardless of ID — other clients (Work/Assist) may use a different ID format.
    const clientPanelAssignments = this.clientPanelAssignments
      .filter(
        (clientPanelAssignment) =>
          isSingleContactPerson
            ? !isElectricityBillingContactPersonId(clientPanelAssignment.contactPersonId)
            : stripElectricityBillingPrefix(clientPanelAssignment.contactPersonId) === contactPersonId
      );

    // Filters generic device assignments for a given contact person.
    const genericDeviceAssignments = this.genericDeviceAssignments
      .filter(
        (genericDeviceAssignment) =>
          isSingleContactPerson
            ? !isElectricityBillingContactPersonId(genericDeviceAssignment.contactPersonId)
            : stripElectricityBillingPrefix(genericDeviceAssignment.contactPersonId) === contactPersonId
      );

    // Combines client panel assignments and generic device assignments.
    const deviceProjectAssignments = [
      ...clientPanelAssignments,
      ...(!this.showCompactVersion ? genericDeviceAssignments : [])
    ];
    
    // Filters independant device assignments and sort them.
    const independantDeviceProjectAssignments = getIndependantDeviceProjectAssignments(
      deviceProjectAssignments
    ).sort((left, right) => compareDeviceProjectAssignments(left, right));

    // Filters dependant device assignments and sort them.
    const dependantDeviceProjectAssignments = getDependantDeviceProjectAssignments(
      deviceProjectAssignments
    ).sort((left, right) => compareDeviceProjectAssignments(left, right));

    const sortedDeviceProjectAssignments: DeviceProjectAssignment[] = [];

    // Combine both type of assignments again in a specific order.
    independantDeviceProjectAssignments.forEach((independantDeviceProjectAssignment) => {
      sortedDeviceProjectAssignments.push(independantDeviceProjectAssignment);
      
      dependantDeviceProjectAssignments.forEach((dependantDeviceProjectAssignment) => {
        if (
          dependantDeviceProjectAssignment.externalElectricityMeterInfo?.assignmentId === independantDeviceProjectAssignment.id
        ) {
          sortedDeviceProjectAssignments.push(dependantDeviceProjectAssignment);
        }
      });
    });

    return sortedDeviceProjectAssignments;
  }

  hyDryDevicesButtonState(contactPerson: ContactPersonInfo) {
    if (this.isReadOnlyMode) {
      return ButtonState.DISABLED;
    }

    if (!this.isDeviceAssignmentInAllowedProjectState) {
      return ButtonState.DISABLED;
    }

    if (!this.canEditAssignments && !this.isAlwaysAllowedToAssignDevices) {
      return ButtonState.DISABLED;
    }

    return this.hasContactPersonDeviceProjectAssignments(contactPerson)
      ? ButtonState.STEP_BUTTON_COMPLETED
      : ButtonState.STEP_BUTTON_INCOMPLETE;
  }
}
